/**
 * `?moderationData=mock` — preview the moderation queue before Brooklyn ships it.
 *
 * Read from the `location` redux slice via `getLocationQuery`, which is how the
 * comments page already drives its filters and pagination — so this behaves like
 * the rest of that surface rather than introducing a second convention.
 */
import { useSelector } from 'react-redux'
import { getLocationQuery } from '../location/index'

export const MODERATION_DATA_PARAM = 'moderationData'

/**
 * Loading and error are previewable too. Happy-path-only fixtures are what let a
 * real loading bug hide in the gaia-web-app Circles mock; don't repeat it.
 */
export type ModerationMockMode = 'mock' | 'mock-loading' | 'mock-error' | null

const RECOGNISED: readonly string[] = ['mock', 'mock-loading', 'mock-error']

// Structural view of exactly the path this selector walks: the location
// slice's query map holds param name → string value. The runtime guards below
// still stand in case a param is repeated or absent.
interface QueryMap {
  get?: (key: string) => string | undefined
}

interface LocationState {
  // Whatever getLocationQuery itself accepts (its JSDoc types the Immutable
  // location Map) — derived, so the two can never drift apart.
  location: Parameters<typeof getLocationQuery>[0]
}

export default function useModerationMockMode (): ModerationMockMode {
  return useSelector((state: LocationState) => {
    const query = getLocationQuery(state.location) as QueryMap | undefined
    const value =
      query && typeof query.get === 'function' ? query.get(MODERATION_DATA_PARAM) : undefined
    return typeof value === 'string' && RECOGNISED.includes(value)
      ? (value as ModerationMockMode)
      : null
  })
}
